import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /**
   * Numero di pagine caricate automaticamente prima di mostrare il bottone
   * "Carica altri". Mantiene il DOM contenuto e lo scroll fluido anche su
   * collezioni lunghe (stile Zalando/ASOS).
   */
  static AUTO_LOAD_LIMIT = 2;

  /** Conteggio pagine auto-caricate nel "ciclo" corrente. */
  #autoLoadCount = 0;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
    this.#updateInfiniteScrollState();

    // Listener delegato per il bottone "Carica altri" (event delegation
    // così funziona anche se il bottone viene re-renderizzato).
    this.addEventListener('click', this.#handleHostClick);

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    this.removeEventListener('click', this.#handleHostClick);
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Click delegato sul host. Intercetta il bottone "Carica altri prodotti".
   */
  #handleHostClick = (event) => {
    const target = event.target;
    if (target && target.classList && target.classList.contains('infinite-scroll-load-more')) {
      event.preventDefault();
      this.#handleLoadMoreClick();
    }
  };

  /**
   * Click sul "Carica altri": esce dalla modalità manuale (resetta il contatore
   * e ri-osserva il sentinel) e renderizza la pagina successiva.
   */
  async #handleLoadMoreClick() {
    this.#exitManualMode();
    await this.#renderNextPage();
  }

  /**
   * Entra in modalità manuale: smette di osservare il sentinel e mostra
   * il bottone "Carica altri" via attributo CSS.
   */
  #enterManualMode() {
    this.setAttribute('manual-load-more', '');
    const { viewMoreNext } = this.refs;
    if (viewMoreNext && this.infinityScrollObserver) {
      this.infinityScrollObserver.unobserve(viewMoreNext);
    }
  }

  /**
   * Esce dalla modalità manuale: resetta il contatore e ri-osserva il sentinel.
   */
  #exitManualMode() {
    this.removeAttribute('manual-load-more');
    this.#autoLoadCount = 0;
    const { viewMoreNext } = this.refs;
    if (viewMoreNext && this.infinityScrollObserver) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * Wrapper chiamato dall'observer al posto di renderNextPage diretto.
   * Carica automaticamente fino ad AUTO_LOAD_LIMIT pagine, poi entra in
   * modalità manuale (mostra il bottone "Carica altri").
   */
  async #handleNextPageObserved() {
    if (this.#autoLoadCount >= PaginatedList.AUTO_LOAD_LIMIT) {
      this.#enterManualMode();
      return;
    }
    this.#autoLoadCount++;
    await this.#renderNextPage();
  }

  /**
   * Mostra/nasconde lo spinner di caricamento in base alla presenza di una pagina
   * successiva. Quando non ci sono altre pagine impostiamo l'attributo
   * `infinite-complete`, così la CSS nasconde lo spinner ed evita che l'utente
   * (specie su connessioni lente) confonda l'attesa con la fine della collezione.
   */
  #updateInfiniteScrollState() {
    const { grid } = this.refs;
    if (!grid) return;

    const lastPage = Number(grid.dataset.lastPage);
    if (!lastPage) {
      this.toggleAttribute('infinite-complete', true);
      return;
    }

    // Leggiamo direttamente dal DOM invece di usare this.refs.cards che
    // potrebbe non essere ancora aggiornato dal MutationObserver dopo
    // un grid.append(). Senza questo, lo spinner resta visibile anche
    // quando la collezione è terminata.
    const allCards = grid.querySelectorAll(':scope > [data-page]');
    const lastCard = allCards[allCards.length - 1];

    if (!lastCard) {
      this.toggleAttribute('infinite-complete', true);
      return;
    }

    const currentPage = Number(lastCard.dataset.page);
    const hasNextPage = currentPage < lastPage;
    this.toggleAttribute('infinite-complete', !hasNextPage);

    // Se la collezione è terminata, smetti di osservare il sentinel
    // per evitare callback inutili dell'IntersectionObserver.
    if (!hasNextPage) {
      const { viewMoreNext } = this.refs;
      if (viewMoreNext && this.infinityScrollObserver) {
        this.infinityScrollObserver.unobserve(viewMoreNext);
      }
    }
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#handleNextPageObserved();
              }
            }
          }
        },
        {
          // Pre-renderizza la pagina successiva con ampio anticipo (~1.5 schermate)
          // così lo scroll continuo non si "blocca" al confine tra una pagina e l'altra.
          rootMargin: '800px 0px 1200px 0px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    this.#updateInfiniteScrollState();

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Reset modalità manuale (un nuovo filtro = nuova esperienza, riparti da zero)
    this.#autoLoadCount = 0;
    this.removeAttribute('manual-load-more');

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // Bug fix spinner infinito dopo filtro: il morph del re-render rimuove
    // l'attributo `infinite-complete` dall'host, ma lo stato veniva ricalcolato
    // SOLO se cambiava data-last-page. Con lo stesso numero di pagine
    // (es. 1 -> 1 filtrando una collezione piccola) lo spinner restava visibile
    // per sempre. Ora lo stato viene ricalcolato a OGNI mutazione della griglia,
    // con un debounce via requestAnimationFrame per aspettare che il morph finisca.
    let refreshScheduled = false;
    const refreshState = () => {
      if (refreshScheduled) return;
      refreshScheduled = true;
      requestAnimationFrame(() => {
        refreshScheduled = false;
        if (!this.isConnected) return;
        this.#observeViewMore();
        this.#updateInfiniteScrollState();
      });
    };

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // La griglia è cambiata: ricalcola sempre lo stato dello spinner
      refreshState();

      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Fallback: dopo 8s smetti di osservare (copre anche connessioni lente)
      // e fai un ultimo ricalcolo dello stato per sicurezza.
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
        refreshState();
      }, 8000);
    }
  };
}
